import React, { useCallback, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Button from '../../../../components/Button';
import { logInternalError } from '../../../../lib/errors';
import {
  RescuePlacementModel,
  rescuePlacementMainQuery,
  RedirectToRescuePlacementHome,
  transferringOwnershipTitle,
  AccountInformation,
} from '../rescuePlacementUtils';
import { gqlTypes } from '../../../../types';
import { gql, useMutation } from '@apollo/client';
import PetView from '../PetView/PetView';
import GoBackLink from '../components/GoBackLink/GoBackLink';
import styles from './PetTemporaryPlacement.module.scss';

const rescueTransferPetTemporarilyMutation = gql`
  mutation ECOMMERCE_rescueTransferPetTemporarily($input: RescueTransferPetTemporarilyInput!) {
    rescueTransferPetTemporarily(input: $input) {
      accountCreated
      newAccountTemporaryPassword
    }
  }
`;

const firstNameLastNameRegex = /([^ ]*)\s*(.*)/;

function TemporaryPlacementSuccess({
  data,
  pet,
  email,
}: {
  data: gqlTypes.ECOMMERCE_rescueTransferPetTemporarily;
  pet: gqlTypes.petDetails;
  email: string;
}) {
  const { t } = useTranslation();
  if (!data.rescueTransferPetTemporarily) {
    return null;
  }

  return (
    <div>
      <h2>{t('success')}</h2>
      <p>
        <Trans
          i18nKey="permission_to_access_pet_name_was_granted_to_email"
          values={{ petName: pet.name, email }}
          components={{ strong: <strong /> }}
        />
      </p>
      {data && data.rescueTransferPetTemporarily && <AccountInformation data={data.rescueTransferPetTemporarily} />}
    </div>
  );
}

function TransferView({ pet }: { pet: gqlTypes.petDetails }) {
  const { t } = useTranslation();
  const [transferPetTemporarily, { loading, error, data }] = useMutation<
    gqlTypes.ECOMMERCE_rescueTransferPetTemporarily,
    gqlTypes.ECOMMERCE_rescueTransferPetTemporarilyVariables
  >(rescueTransferPetTemporarilyMutation, {
    refetchQueries: [{ query: rescuePlacementMainQuery }],
  });

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const onSubmit = useCallback(() => {
    if (loading) {
      return;
    }

    const nameMatch = name.match(firstNameLastNameRegex);
    const firstName = (nameMatch && nameMatch[1]) || '';
    const lastName = (nameMatch && nameMatch[2]) || null;
    transferPetTemporarily({
      variables: {
        input: {
          email,
          firstName,
          lastName,
          petId: pet.id,
        },
      },
    }).catch((err) => {
      logInternalError(err);
    });
  }, [email, loading, name, pet.id, transferPetTemporarily]);

  if (data) {
    return <TemporaryPlacementSuccess data={data} pet={pet} email={email} />;
  }

  return (
    <div className={styles.transferView}>
      <input
        type="text"
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
        placeholder={t('email')}
        data-hj-whitelist
      />
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        placeholder={t('name')}
        data-hj-whitelist
      />
      <Button onClick={onSubmit}>{t('transfer_to_a_new_foster_parent')}</Button>
      {error && <div className={styles.textDanger}>{error.message}</div>}
    </div>
  );
}

export default function PetTemporaryPlacement({ model, petId }: { model: RescuePlacementModel; petId: string }) {
  const { t } = useTranslation();
  const pet = model.getPetById(petId);

  if (!pet) {
    return <RedirectToRescuePlacementHome />;
  }

  return (
    <div className={styles.main}>
      <GoBackLink />
      <PetView pet={pet} title={transferringOwnershipTitle(pet, t)} />
      <p>{t('enter_the_information_for_the_foster_where_pet_name_is_being_placed', { petName: pet.name })}</p>
      <TransferView pet={pet} />
    </div>
  );
}
